import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { IconButton, useTheme } from "@mui/material";
import { CancelOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import IconConstants from "../.././utils/IconConstants";
import { createAssetSchema } from "./addAssetFormConfig";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";
import { errorToster, successToster } from "../../shared/toster/Toster";
import { Helper } from "../../utils/Helpers";
import { format } from "date-fns";
import { Can, FEATURES, ACTIONS } from "../../core/permissions";

const AddAsset = ({
  open,
  handleClose,
  editData,
  setRefresh,
  setOpen,
  setEditData,
}) => {
  const {
    handleSubmit,
    control,
    reset,
    register,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createAssetSchema),
    defaultValues: editData || {
      asset_name: "",
      asset_code: "",
      asset_status: "1",
      asset_image: "",
      asset_category: "",
      room_id: "",
      brand: "",
      model: "",
      description: "",
      vendor_name: "",
      po_number: "",
      invoice_number: "",
      purchase_price: "",
      invoice_date: "",
      purchase_date: "",
      warrenty_vendor: "",
      insurance_start_date: "",
      insurance_end_date: "",
      warrenty_start_date: "",
      warrenty_end_date: "",
    },
  });
  const formStatusConsole = {
    success: editData ? "updated" : "added",
    error: editData ? "update" : "add",
  };
  const { api } = ApiClient();
  const { palette } = useTheme();
  const [assetCategories, setAssetCategories] = useState([]);
  const [roomNumbers, setRoomNumbers] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [newCategory, setNewCategory] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const [imageFile, setImageFile] = useState(null);

  const { addWithPadding, getLocalTimeFromUtc, getGradientTextStyle } = new Helper();

  useEffect(() => {
    if (editData) {
      reset({
        asset_name: editData.asset_name,
        asset_code: editData.asset_code,
        asset_status: editData.asset_status,
        room_id: editData.room_id,
        asset_category: editData.asset_category,
        brand: editData.brand,
        model: editData.model,
        description: editData.description,
        vendor_name: editData.vendor_name,
        po_number: editData.po_number,
        invoice_number: editData.invoice_number,
        purchase_price: editData.purchase_price,
        invoice_date: getLocalTimeFromUtc(editData.invoice_date)?.dateString,
        purchase_date: getLocalTimeFromUtc(editData.purchase_date)?.dateString,
        insurance_start_date: getLocalTimeFromUtc(editData.insurance_start_date)
          ?.dateString,
        insurance_end_date: getLocalTimeFromUtc(editData.insurance_end_date)
          ?.dateString,
        warrenty_start_date: getLocalTimeFromUtc(editData.warrenty_start_date)
          ?.dateString,
        warrenty_end_date: getLocalTimeFromUtc(editData.warrenty_end_date)
          ?.dateString,
        warrenty_vendor: editData.warranty_vendor,
      });
      if (editData.asset_image) {
        setSelectedImage(editData.asset_image);
      }
    }
  }, [editData, assetCategories, reset]);

  const assetCategory = watch("asset_category");

  const assetLocation = watch("room_id");

  const assetCode = watch("asset_code");

  useEffect(() => {
    if (!editData?.asset_code) {
      if (assetCategory && assetLocation && assetCategories && roomNumbers) {
        let category = assetCategories.find(
          (item) => item.value === assetCategory
        )?.label;
        if (!category) {
          category = assetCategory;
          setNewCategory(true);
        }
        if (typeof category === "string" && category.length > 0)
          category = category.length > 3 ? category.slice(0, 3) : category;

        let code = `${category.toLowerCase()}-001`;

        const checkAssetCode = (code) => {
          let dupCode = allAssets.find(
            (item) => item.asset_code == code
          )?.asset_code;
          if (dupCode) {
            let temp = dupCode.split("-");
            temp[1] = addWithPadding(Number(temp[1]), 1, 3);
            code = `${temp[0]}-${temp[1]}`;
            return checkAssetCode(code);
          } else {
            return code;
          }
        };
        let asset_code = checkAssetCode(code);

        setValue("asset_code", asset_code);
        trigger("asset_code");
      }
    }
  }, [assetCategory, assetLocation]);

  useEffect(() => {
    fetchAssetCategories();
    fetchAllRooms();
    fetchAllAssets();
  }, []);

  const fetchAllAssets = async () => {
    let response = await api.get(
      `${END_POINT.ASSET.GET_ALL_ASSETS}?page=${1}&limit=${10000}`
    );

    const { assets, total } = response.data.data;

    setAllAssets(assets);
  };

  const fetchAssetCategories = async () => {
    let categories = await api.get(END_POINT.ASSET.GET_ALL_CATEGORIES);
    const optionCategories = categories.data.data.map((item) => ({
      label: item.asset_category_name.toString().toUpperCase(),
      value: item.asset_category_id,
    }));

    setAssetCategories(optionCategories);
  };

  const fetchAllRooms = async () => {
    let all_rooms = await api.get(END_POINT.ROOM.GET_ALL_ROOM_IN_HOTEL);
    const optionRooms = all_rooms.data.data.map((item) => ({
      label: `Room - ${item.room_no}`,
      value: item.room_id,
    }));

    setRoomNumbers(optionRooms);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/svg+xml",
        "image/bmp",
        "image/tiff",
      ];

      if (!allowedTypes.includes(file.type)) {
        errorToster("Only JPEG, PNG, and GIF images are allowed");

        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        errorToster("File size must be under 5MB");

        return;
      }

      setSelectedImage(URL.createObjectURL(file));

      setImageFile(file);
    } else {
      setSelectedImage(null);

      setImageFile(null);
    }
  };

  const handleImageClick = () => {
    document.getElementById("image-upload").click();
  };

  const addNewCategory = async () => {
    let formData;
    try {
      const category_data = {
        data: {
          category_name: assetCategory.toString().toLowerCase(),
        },
      };
      const response = await api.post(
        END_POINT.ASSET.ADD_NEW_CATEGORY,
        category_data,
        "Error adding new category"
      );

      if (response.status === 200) {
        successToster("Asset Category added successfully!");

        fetchAssetCategories();

        formData = {
          asset_category:
            response.data.data.assetCategoryRes[0].asset_category_id,
        };
      } else {
        errorToster("Failed to add asset. Please try again.");
      }
    } catch (error) {
      errorToster("Something went wrong while adding new category");
    } finally {
      setNewCategory(false);

      return formData;
    }
  };
  const onSubmit = async (data) => {
    data = { ...data, asset_code: assetCode };

    if (newCategory) {
      let d = await addNewCategory();
      if (d) {
        data.asset_category = d.asset_category;
      }
    }

    if (data) {
      data.invoice_date &&= format(new Date(data.invoice_date), "yyyy-MM-dd");
      data.purchase_date &&= format(new Date(data.purchase_date), "yyyy-MM-dd");
      data.insurance_start_date &&= format(
        new Date(data.insurance_start_date),
        "yyyy-MM-dd"
      );
      data.insurance_end_date &&= format(
        new Date(data.insurance_end_date),
        "yyyy-MM-dd"
      );
      data.warrenty_start_date &&= format(
        new Date(data.warrenty_start_date),
        "yyyy-MM-dd"
      );
      data.warrenty_end_date &&= format(
        new Date(data.warrenty_end_date),
        "yyyy-MM-dd"
      );
    }

    try {
      const FormData = require("form-data");
      const formDataInstance = new FormData();
      data.asset_status_id = Number(data.asset_status);

      formDataInstance.append("data", JSON.stringify({ ...data }));
      // Append image file if it exists

      if (imageFile) {
        formDataInstance.append("assetImage", imageFile);
      } else if (editData?.image) {
        formDataInstance.append("assetImage", editData.image);
      }
      // Send FormData to API
      const response = await api.postFormData(
        END_POINT.ASSET.ADD_ASSET,
        formDataInstance,
        "ERROR ADDING OR UPDATING ASSET"
      );
      if (response.status === 200) {
        successToster(`Asset ${formStatusConsole.success} successfully!`);
        reset();
        setRefresh((prev) => !prev);
        setEditData(null);
        setOpen(false);
      } else {
        errorToster(
          `Failed to  asset ${formStatusConsole.error}. Please try again.`
        );
      }
    } catch (error) {
      errorToster("Something went wrong");
    }
  };

  const commonStyles = {
    fontSize: { xs: "2.564vw", sm: "1.92vw", md: "1.111vw" },
  };

  const textFieldStyles = {
    input: { sx: commonStyles },
    label: { sx: { ...commonStyles, color: "#6F6F6F" } },
    helperText: {
      sx: {
        fontSize: { xs: "2.564vw", sm: "1.44vw", md: "0.83vw" },
      },
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4} sx={{ alignItems: "stretch" }}>
          {/* Left Column (60% width) */}
          <Grid item xs={12} sm={7}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "2.564vw", sm: "1.92vw", md: "1.389vw" },
                    ...getGradientTextStyle(palette)
                  }}
                >
                  Asset Info
                </Typography>
              </Grid>
              {/* First Row */}
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register("asset_name")}
                  error={!!errors.asset_name}
                  helperText={errors.asset_name?.message}
                  label="Asset Name"
                  fullWidth
                  size="small"
                  InputProps={textFieldStyles.input}
                  InputLabelProps={textFieldStyles.label}
                  FormHelperTextProps={textFieldStyles.helperText}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="asset_category"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      freeSolo
                      {...field}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: {
                            xs: "2.564vw",
                            sm: "1.92vw",
                            md: "1.111vw",
                          },
                        },
                        ".MuiAutocomplete-noOptions": {
                          fontSize: { xs: "2.564vw", sm: "1.8vw", md: "0.5vw" },
                        },
                      }}
                      ListboxProps={{
                        sx: {
                          fontSize: { xs: "2.564vw", sm: "1.8vw", md: "1vw" },
                          ".MuiAutocomplete-noOptions": {
                            fontSize: {
                              xs: "2.564vw",
                              sm: "1.8vw",
                              md: "0.5vw",
                            },
                          },
                        },
                      }}
                      {...field}
                      options={assetCategories}
                      getOptionLabel={(option) => option?.label ?? ""}
                      value={
                        assetCategories.find(
                          (opt) => opt.value === field.value
                        ) || null
                      }
                      onChange={(_, value) => {
                        field.onChange(value?.value || null);
                        if (typeof value === "string") {
                          setValue("asset_category", value); // Manually update state
                        } else {
                          setValue("asset_category", value?.value || "");
                        }
                      }}
                      onInputChange={(_, newInputValue) => {
                        if (newInputValue !== field.value) {
                          setValue("asset_category", newInputValue); // Manually triggerupdate
                        }
                        field.onChange(newInputValue);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="Asset Category"
                          error={!!errors.asset_category}
                          helperText={errors.asset_category?.message}
                          InputLabelProps={textFieldStyles.label}
                          FormHelperTextProps={textFieldStyles.helperText}
                          InputProps={{
                            ...params.InputProps,
                            readOnly: editData?.asset_category ? true : false, // Make the input read-only
                          }}
                        />
                      )}
                      readOnly={editData?.asset_category ? true : false}
                      disableClearable={editData?.asset_category ? true : false}
                      disabled={editData?.asset_category ? true : false}
                    />
                  )}
                />
              </Grid>
              {/* Second Row */}

              <Grid item xs={6} mt={1}>
                <Controller
                  name="room_id"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: {
                            xs: "2.564vw",
                            sm: "1.92vw",
                            md: "1.111vw",
                          },
                        },
                        ".MuiAutocomplete-noOptions": {
                          fontSize: { xs: "2.564vw", sm: "1.8vw", md: "0.5vw" },
                        },
                      }}
                      ListboxProps={{
                        sx: {
                          fontSize: { xs: "2.564vw", sm: "1.8vw", md: "1vw" },
                          ".MuiAutocomplete-noOptions": {
                            fontSize: {
                              xs: "2.564vw",
                              sm: "1.8vw",
                              md: "0.5vw",
                            },
                          },
                        },
                      }}
                      {...field}
                      options={roomNumbers} // Replace with your list of locations
                      getOptionLabel={(option) => option?.label ?? ""}
                      value={
                        roomNumbers.find((opt) => opt.value === field.value) ||
                        null
                      }
                      onChange={(_, value) =>
                        field.onChange(value?.value || null)
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="Asset Location"
                          error={!!errors.room_id}
                          helperText={errors.room_id?.message}
                          InputLabelProps={textFieldStyles.label}
                          FormHelperTextProps={textFieldStyles.helperText}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} mt={1}>
                <TextField
                  {...register("asset_code")}
                  error={!!errors.asset_code}
                  helperText={errors.asset_code?.message}
                  label="Code"
                  fullWidth
                  size="small"
                  InputProps={{
                    ...textFieldStyles.input,
                    readOnly: true, // Make the input read-only
                  }}
                  InputLabelProps={{
                    ...textFieldStyles.label,
                    shrink: true, // Force the label to always stay at the top
                  }}
                  FormHelperTextProps={textFieldStyles.helperText}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register("brand")}
                  error={!!errors.brand}
                  helperText={errors.brand?.message}
                  label="Brand"
                  fullWidth
                  size="small"
                  InputProps={textFieldStyles.input}
                  InputLabelProps={textFieldStyles.label}
                  FormHelperTextProps={textFieldStyles.helperText}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register("model")}
                  error={!!errors.model}
                  helperText={errors.model?.message}
                  label="Model"
                  fullWidth
                  size="small"
                  InputProps={textFieldStyles.input}
                  InputLabelProps={textFieldStyles.label}
                  FormHelperTextProps={textFieldStyles.helperText}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  {...register("description")}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  label="Description"
                  fullWidth
                  multiline
                  size="small"
                  InputProps={textFieldStyles.input}
                  InputLabelProps={textFieldStyles.label}
                  FormHelperTextProps={textFieldStyles.helperText}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column (40% width) */}
          <Grid item xs={12} sm={5} order={{ xs: -1, sm: 0 }}>
            <Typography
              variant="body2"
              gutterBottom
              color="textSecondary"
              sx={{
                ml: { xs: 14, sm: 0 },
                fontWeight: 500,
                fontSize: { xs: "2.564vw", sm: "1.92vw", md: "1.111vw" },
              }}
            ></Typography>
            <Box
              sx={{
                height: "1vh",
                minHeight: { xs: "35vw", sm: "22vw", md: "14vw" },
                width: { xs: "40vw", sm: "35vw", md: "85%" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#FFF6E8",
                border: "2px dashed #FFCE5C",
                borderRadius: "8px",
                cursor: "pointer",
                ml: { xs: 10, sm: 0 },
                p: 2,
                position: "relative",
                mt: 2,
              }}
              onClick={handleImageClick}
            >
              {selectedImage ? (
                <>
                  <img
                    src={selectedImage}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "110%",
                      objectFit: "fill",
                      borderRadius: "4px",
                    }}
                  />

                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(null);
                      setImageFile(null);
                    }}
                    sx={{
                      height: { xs: "5vw", sm: "3vw" },
                      width: { xs: "5vw", sm: "3vw" },
                      position: "absolute",
                      top: 1,
                      right: 8,
                      backgroundColor: "rgb(255, 255, 255)",
                      "&:hover": {
                        backgroundColor: "rgb(255, 255, 255)",
                      },
                    }}
                  >
                    <CancelOutlined
                      sx={{
                        color: "rgba(196, 196, 196, 0.66)",
                        fontSize: { xs: "5vw", sm: "3vw" },
                      }}
                    />
                  </IconButton>
                </>
              ) : (
                <Box textAlign="center">
                  <img src={IconConstants.AddImageIcon} alt="Add" />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ display: { xs: "none", sm: "inherit" } }}
                  >
                    Click to upload asset image
                  </Typography>
                </Box>
              )}
            </Box>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
            {errors.image && (
              <Typography
                color="error"
                sx={{
                  fontSize: { xs: "2.564vw", sm: "1.44vw", md: "0.83vw" },
                  mt: 1,
                }}
              >
                {errors.asset_image.message}
              </Typography>
            )}
          </Grid>

          {/* Purchase Info Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "2.564vw", sm: "1.92vw", md: "1.389vw" },
                ...getGradientTextStyle(palette)
              }}
            >
              Purchase Info
            </Typography>
          </Grid>

          <Grid container spacing={2} sx={{ px: 3 }}>
            {[
              "vendor_name",
              "po_number",
              "invoice_number",
              "purchase_price",
            ].map((field) => (
              <Grid item xs={12} sm={4} key={field} mt={1}>
                <TextField
                  {...register(field)}
                  error={!!errors[field]}
                  helperText={errors[field]?.message}
                  label={field.split("_").join(" ").toUpperCase()}
                  fullWidth
                  size="small"
                  InputProps={textFieldStyles.input}
                  InputLabelProps={textFieldStyles.label}
                  FormHelperTextProps={textFieldStyles.helperText}
                />
              </Grid>
            ))}

            {["invoice_date", "purchase_date"].map((field) => (
              <Grid item xs={12} sm={4} key={field} mt={1}>
                <TextField
                  {...register(field)}
                  error={!!errors[field]}
                  helperText={errors[field]?.message}
                  label={field.split("_").join(" ").toUpperCase()}
                  fullWidth
                  type="date"
                  size="small"
                  InputLabelProps={{ ...textFieldStyles.label, shrink: true }}
                  InputProps={textFieldStyles.input}
                />
              </Grid>
            ))}
          </Grid>

          {/* Warranty Info Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "2.564vw", sm: "1.92vw", md: "1.389vw" },
                ...getGradientTextStyle(palette)
              }}
            >
              Warranty Info
            </Typography>
          </Grid>

          <Grid container spacing={2} sx={{ px: 3 }}>
            {["warrenty_vendor"].map((field) => (
              <Grid item xs={12} sm={4} key={field} mt={1}>
                <TextField
                  {...register(field)}
                  error={!!errors[field]}
                  helperText={errors[field]?.message}
                  label={field.split("_").join(" ").toUpperCase()}
                  fullWidth
                  size="small"
                  InputProps={textFieldStyles.input}
                  InputLabelProps={textFieldStyles.label}
                  FormHelperTextProps={textFieldStyles.helperText}
                />
              </Grid>
            ))}

            {[
              "insurance_start_date",
              "insurance_end_date",
              "warrenty_start_date",
              "warrenty_end_date",
            ].map((field) => (
              <Grid item xs={12} sm={4} key={field} mt={1}>
                <TextField
                  {...register(field)}
                  error={!!errors[field]}
                  helperText={errors[field]?.message}
                  label={field.split("_").join(" ").toUpperCase()}
                  fullWidth
                  type="date"
                  size="small"
                  InputLabelProps={{ ...textFieldStyles.label, shrink: true }}
                  InputProps={textFieldStyles.input}
                  FormHelperTextProps={textFieldStyles.helperText}
                />
              </Grid>
            ))}
          </Grid>

          {/* Form Buttons */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={6} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Can
                  feature={FEATURES.INVENTORY_ITEMS}
                  action={editData ? ACTIONS.UPDATE : ACTIONS.CREATE}
                >
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                  >
                    {editData ? "Save" : "Submit"}
                  </Button>
                </Can>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AddAsset;
